import axios from "axios";

const API_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:3001/api/v1"; // Your Rails API base URL

// Function to create or find a user by username
export const findOrCreateUser = async (username) => {
  try {
    // Rails typically uses POST for create, but if your :users endpoint is idempotent for :create on existing, this is fine.
    // Or, you might have a specific GET /users?username=... and then POST if not found.
    // For simplicity, assuming POST /users handles both finding or creating.
    const response = await axios.post(`${API_URL}/users`, 
      { user: { username } },
      { headers: { 'Content-Type': 'application/json' } } // Explicitly set Content-Type
    );
    // Adjust to extract user from the nested structure returned by render_success
    if (response.data && response.data.success && response.data.data && response.data.data.user) {
      return response.data.data.user; 
    } else {
      // Handle cases where the structure might be different or an error wasn't caught by axios
      throw new Error(response.data.error || 'User data not found in expected format');
    }
  } catch (error) {
    console.error("Error creating/finding user:", error.response ? error.response.data : error.message);
    throw error;
  }
};

// Function to create a new game room
export const createGameRoom = async (userId) => {
  try {
    // Assuming the backend expects the creator's user_id or handles it via current_user if auth was set up
    // For now, let's assume we might need to pass it if user creation is separate.
    // If your backend automatically assigns the creator, you might not need to pass userId.
    const response = await axios.post(`${API_URL}/game_rooms`, { user_id: userId }); // Adjust payload as per your backend
    // Response will now contain game_room.code
    if (response.data && response.data.success && response.data.data && response.data.data.game_room) {
      return response.data.data.game_room; // This game_room object includes the .code
    } else {
      throw new Error(response.data.error || 'Game room data not found in expected format after create');
    }
  } catch (error) {
    console.error("Error creating game room:", error.response ? error.response.data : error.message);
    throw error;
  }
};

// Function to join an existing game room by code
export const joinGameRoom = async (roomCode, userId) => {
  try {
    const response = await axios.post(`${API_URL}/game_rooms/${roomCode}/join`, { user_id: userId });
    if (response.data && response.data.success && response.data.data && response.data.data.game_room) {
      return response.data.data.game_room;
    } else {
      throw new Error(response.data.error || 'Joined game room data not found in expected format');
    }
  } catch (error) {
    console.error("Error joining game room:", error.response ? error.response.data : error.message);
    throw error;
  }
};

// Function to get the state of a game room by code
export const getGameRoomState = async (roomCode, userId) => {
  const response = await axios.get(`${API_URL}/game_rooms/${roomCode}/state`, {
    headers: { 'X-User-ID': userId }
  });
  // Assuming the backend now returns game_state within data, and game_room within game_state will have the code
  return response.data.data.game_state; 
};

// Function to start the game by code
export const startGame = async (roomCode, userId) => {
  try {
    const response = await axios.post(`${API_URL}/game_rooms/${roomCode}/start_game`, { user_id: userId });
    return response.data.data.game_state;
  } catch (error) {
    console.error("Error starting game:", error.response?.data?.error || error.message);
    throw new Error(error.response?.data?.error || 'Failed to start game');
  }
};

export const playCard = async (roomCode, userId, card, chosenColor = null) => {
  try {
    const payload = {
      user_id: userId,
      card: {
        color: card.color,
        value: card.value,
        card_type: card.card_type,
      },
    };
    if (chosenColor) {
      payload.chosen_color = chosenColor;
    }
    const response = await axios.post(`${API_URL}/game_rooms/${roomCode}/play_card`, payload);
    // play_card response structure: { success: true, card_played: card, (maybe game_state or winner info) }
    // Actual game state updates will come via Action Cable.
    // This response can confirm the immediate result of the action.
    return response.data.data; // Assuming backend wraps successful responses in `data` 
  } catch (error) {
    console.error("Error playing card:", error.response?.data?.error || error.message);
    throw new Error(error.response?.data?.error || 'Failed to play card');
  }
};

export const drawCard = async (roomCode, userId) => {
  try {
    const response = await axios.post(`${API_URL}/game_rooms/${roomCode}/draw_card`, { user_id: userId });
    // Backend response: { success: true, card_drawn: card, can_play: boolean, (maybe game_state) }
    // Actual game state updates (hand, turn) will come via Action Cable.
    // This response can be used for immediate feedback, e.g., showing the drawn card before full state sync.
    return response.data.data; // Assuming backend wraps successful responses in `data`
  } catch (error) {
    console.error("Error drawing card:", error.response?.data?.error || error.message);
    throw new Error(error.response?.data?.error || 'Failed to draw card');
  }
};

// Add other API calls here as needed (e.g., drawCard)
